import { memo } from 'react';
import { SVGIcon } from '../SVGIcon';
import type { TireData } from './TiresControl.types';

type IconType = 'tire' | 'rim';

interface TireIconWithStatusProps {
  tire: TireData;
  type: IconType;
  size?: 'sm' | 'md';
}

type StatusVariant = 'success' | 'warning' | 'danger' | null;

/**
 * Określa status ikony opony na podstawie replacementStatus
 */
const getTireStatusVariant = (tire: TireData): StatusVariant => {
  if (!tire.replacementStatus) return null;

  switch (tire.replacementStatus) {
    case 'Nie':
      return 'success';
    case 'Ostatni sezon':
      return 'warning';
    case 'Tak':
      return 'danger';
    default:
      return null;
  }
};

/**
 * Określa status ikony felgi na podstawie rimConditionStatus (treadConditionType z API)
 */
const getRimStatusVariant = (tire: TireData): StatusVariant => {
  if (!tire.rimConditionStatus) return null;

  switch (tire.rimConditionStatus) {
    case 'OK':
      return 'success';
    case 'Uszkodzone':
      return 'warning';
    case 'Wielokrotne uszkodzenia':
      return 'danger';
    default:
      return null;
  }
};

/**
 * Komponent ikony opony/felgi ze znacznikiem statusu
 * - success (zielona fajka) - OK / nie wymaga wymiany
 * - warning (żółty trójkąt) - ostatni sezon / uszkodzone krawędzie
 * - danger (czerwony wykrzyknik) - wymaga wymiany / wielokrotne uszkodzenia
 */
export const TireIconWithStatus = memo(({ tire, type, size = 'md' }: TireIconWithStatusProps) => {
  const iconName = type === 'tire' ? 'tires' : 'wheel';
  const iconSize = size === 'sm' ? 24 : 28;
  const containerSize = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
  const statusSize = size === 'sm' ? 16 : 20;

  // Użyj odpowiedniej funkcji w zależności od typu ikony
  const status = type === 'tire'
    ? getTireStatusVariant(tire)
    : getRimStatusVariant(tire);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <SVGIcon name="check-circle" fill="white" size={statusSize} />;
      case 'warning':
        return <SVGIcon name="warning-triangle" fill="yellow" size={statusSize} />;
      case 'danger':
        return <SVGIcon name="warn-circle" fill="white" size={statusSize} />;
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      <div className={`flex justify-center items-center ${containerSize} rounded-[10px] bg-primary`}>
        <SVGIcon name={iconName} size={iconSize} fill="white" />
      </div>
      {status && (
        <div className="absolute -top-2 -right-2">
          {getStatusIcon()}
        </div>
      )}
    </div>
  );
});
